import { Pipe, PipeTransform, inject } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Observable, Subscriber, throwError } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { LOADING_RULE } from 'shared/util-common';

import { PrettifyResult } from '@treetop/shared/model';

import { RuleService } from '../infrastructure/rule.service';

@Pipe({
  name: 'prettifyRule',
  standalone: true,
})
export class PrettifyRulePipe implements PipeTransform {
  ruleService = inject(RuleService);

  transform(rule: UntypedFormControl | string, publishedComponents?: boolean): Observable<PrettifyResult> {
    return new Observable((observer) => {
      if (!rule) {
        observer.next({
          original: '',
          prettified: '',
        });
        return;
      }

      observer.next({
        original: '',
        prettified: LOADING_RULE,
      });

      if (typeof rule === 'string') {
        this.transformString(rule, observer);
      } else if (rule instanceof UntypedFormControl) {
        this.transformFormControl(rule, observer, publishedComponents);
      }
    });
  }

  private transformString(rule: string, observer: Subscriber<PrettifyResult>): void {
    this.ruleService
      .prettifyRule(rule)
      .pipe(
        catchError((err) => {
          return throwError(() => err);
        }),
      )
      .subscribe((result: PrettifyResult) => {
        observer.next(result);
      });
  }

  private transformFormControl(rule: UntypedFormControl, observer: Subscriber<PrettifyResult>, publishedComponents?: boolean): void {
    // Initialization of 'prettifyRule' Pipe.
    this.ruleService
      .prettifyRule(rule.value, publishedComponents)
      .pipe(
        catchError((err) => {
          return throwError(() => err);
        }),
      )
      .subscribe((result: PrettifyResult) => {
        observer.next(result);
      });

    // Update 'prettifyRule' Pipe with debounce-time when value of rule changes.
    rule.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(() => {
          observer.next({
            original: '',
            prettified: LOADING_RULE,
          });
          return this.ruleService.prettifyRule(rule.value, publishedComponents);
        }),
        catchError((err) => {
          return throwError(() => err);
        }),
      )
      .subscribe((result: PrettifyResult) => {
        observer.next(result);
      });
  }
}
